import { ComponentIds, Entity } from '@inter-ikea-core/core';
import {
  QuestionSheetState,
  setQuestionSheetVisible,
  setQuestionSheetVisibleWithProps
} from 'ducks/questionSheet';
import { BehaviorParameters } from 'rangeConfig/@types';
import {
  addFrontStyleToFrame,
  frameHasFronts,
  getFrontsInScene,
  replaceFrontStyle,
  getPartiallyOpenFrameSize
} from '../utils/frontLayouts';
import keepGlassDoorsImage from 'ui/images/KeepGlassDoors.jpg';
import keepOpenFrames from 'ui/images/KeepOpenFrames.jpg';
import keepSolidDoors from 'ui/images/KeepSolidDoors.jpg';
import keepSolidDoorsPlatsa from 'ui/images/KeepSolidDoorsPlatsa.jpg';
import { ProductInfoComponent, ProductType } from '@inter-ikea-storageone/range';
import { setKeepGlassDoors, setKeepOpenFrames, setKeepSolidDoors } from 'ducks/changeFronts';
import { getRange } from 'utils/queryUtils';
import { reportChangeSolutionFronts, reportDoorPromptReply } from 'analytics/reporters';
import keepMirrorDoorsImg from 'ui/images/KeepMirrorDoorsImg.jpg';

const changeFrontBehaviour = ({ coreApi, dispatch, props, rootState }: BehaviorParameters) => {
  const selectedFrontId =
    props.coreProductIds?.length > 0 ? props.coreProductIds[0] : props.coreProductId;
  const frames = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);
  const frontEntitesInScene = getFrontsInScene(coreApi);
  const openFrames = getAllOpenFrames();
  const partiallyOpenFrames = getAllPartiallyOpenFrames();
  const products = rootState.products?.inMarket;
  const frontTypes = [
    ProductType.PLATSA_HINGE_DOOR_ASM,
    ProductType.PLATSA_SC_HINGE_DOOR_ASM,
    ProductType.PLATSA_DRAWER_ASM,
    ProductType.BESTA_DOOR_38_ASM,
    ProductType.BESTA_DOOR_64_ASM,
    ProductType.BESTA_DRAWER_26_ASM,
    ProductType.SMASTAD_HINGE_DOOR_ASM,
    ProductType.SMASTAD_DRAWER_ASM
  ];

  type FrontTypesInSceneType = {
    glass: number;
    other: number;
  };

  if (frontEntitesInScene?.length === 0) {
    applySelectedFrontToFrames(frames, partiallyOpenFrames);
    return;
  }

  handleFrontsInScene();

  function handleFrontsInScene() {
    const selectedDoorIsGlass = coreApi.products.isGlassDoor(selectedFrontId, products);
    const frontTypesInScene = getFrontTypesInScene();

    if (frontTypesInScene.glass > 0 && frontTypesInScene.other > 0) {
      if (selectedDoorIsGlass && frontTypesInScene.other > 0) {
        showChangeSolidDoorsSheet();
      } else if (!selectedDoorIsGlass && frontTypesInScene.glass > 0) {
        getRange() === 'platsa' ? showKeepMirrorDoorsSheet() : showChangeGlassDoorsSheet();
      } else {
        applySelectedFrontToAllFramesThasHasDoors();
        closeSheetOrOpenFramesSheet();
      }
    } else {
      applySelectedFrontToAllFramesThasHasDoors();
      closeSheetOrOpenFramesSheet();
    }
  }

  function applySelectedFrontToFrames(openFrames: Entity[], partiallyOpenFrames: Entity[]) {
    const frontProduct = products.find(product => product.coreProductId === selectedFrontId);

    if (frontProduct && (openFrames.length || partiallyOpenFrames.length) > 0) {
      openFrames.forEach(frame => {
        addFrontStyleToFrame(frontProduct, frame, products, coreApi);
      });

      partiallyOpenFrames.forEach(frame => {
        if (coreApi.entities.isProductType(frame, ProductType.SMASTAD_LOFT_BED)) {
          // Handle SMÅSTAD loft bed separately since it has two "compartments" with different heights
          const frontsOnFrame = coreApi.entities
            .getFrameDescendants(frame)
            .filter(
              child =>
                child.hasComponent(ComponentIds.DOOR_COMPONENT_ID) ||
                child.hasComponent(ComponentIds.DRAWER_COMPONENT_ID)
            );
          coreApi.entities.deleteMultiple(frontsOnFrame);
          addFrontStyleToFrame(frontProduct, frame, products, coreApi);
        } else {
          const partiallyOpenFrame = getPartiallyOpenFrameSize(coreApi, frame);

          const frontProductForPartiallyOpenFrames = products.find(
            product =>
              product.communicatedDimensions.height === partiallyOpenFrame.height &&
              product.name === frontProduct.name &&
              frontTypes.includes(product.type) &&
              product.design === frontProduct.design &&
              (product.communicatedDimensions.width === partiallyOpenFrame.width ||
                product.communicatedDimensions.width === partiallyOpenFrame.width / 2)
          );

          if (frontProductForPartiallyOpenFrames) {
            coreApi.products.addToTarget(frontProductForPartiallyOpenFrames.coreProductId, frame);
            if (
              partiallyOpenFrame.width !==
              frontProductForPartiallyOpenFrames.communicatedDimensions.width
            ) {
              coreApi.products.addToTarget(frontProductForPartiallyOpenFrames.coreProductId, frame);
            }
          }
        }
      });

      reportChangeSolutionFronts({
        color: `${frontProduct.name} ${frontProduct.design}`
      });
    } else if (frontProduct) {
      // Trigger an add we know will fail to get the placementAttempt and provide user feedback
      coreApi.products.add(frontProduct.coreProductId);
    }
  }

  function replaceFrontsWithSelectedFront(frontsToReplace: Entity[]) {
    const frontProduct = products.find(product => product.coreProductId === selectedFrontId);
    if (frontProduct && frontsToReplace.length > 0) {
      coreApi.history.historyAction(() => {
        frontsToReplace.forEach(frontToReplace => {
          replaceFrontStyle(frontProduct, frontToReplace, products, coreApi);
        });
      });
      reportChangeSolutionFronts({
        color: `${frontProduct.name} ${frontProduct.design}`
      });
    }
  }

  function applySelectedFrontToAllFramesThasHasDoors() {
    replaceFrontsWithSelectedFront(frontEntitesInScene);
    closeSheetOrOpenFramesSheet();
  }

  function setSolidDoorsAndKeepGlass() {
    const solidFronts = getGlassOrSolidFrontsInScene(false);
    replaceFrontsWithSelectedFront(solidFronts);
    dispatch(setKeepGlassDoors(true));
    closeSheetOrOpenFramesSheet();
  }

  function setGlassDoorsAndKeepSolid() {
    const glassFronts = getGlassOrSolidFrontsInScene(true);
    replaceFrontsWithSelectedFront(glassFronts);
    dispatch(setKeepSolidDoors(true));
    closeSheetOrOpenFramesSheet();
  }

  function getGlassOrSolidFrontsInScene(glass: boolean): Entity[] {
    return frontEntitesInScene.reduce((agg, front) => {
      if (
        glass
          ? front
              .getComponent<ProductInfoComponent>(ProductInfoComponent.id)
              ?.design.includes('glass')
          : !front
              .getComponent<ProductInfoComponent>(ProductInfoComponent.id)
              ?.design.includes('glass')
      ) {
        agg.push(front);
      }
      return agg;
    }, [] as Entity[]);
  }

  function closeSheetOrOpenFramesSheet() {
    if ((openFrames.length || partiallyOpenFrames.length) > 0) {
      showKeepOpenShelvesSheet();
    } else {
      dispatch(setQuestionSheetVisible(false));
    }
  }

  function onAcceptKeepOpenShelvesSheet() {
    dispatch(setKeepOpenFrames(true));
    dispatch(setQuestionSheetVisible(false));
  }

  function setDoorsOnOpenFrames() {
    dispatch(setKeepOpenFrames(false));
    applySelectedFrontToFrames(openFrames, partiallyOpenFrames);
    dispatch(setQuestionSheetVisible(false));
  }

  function setGlassDoorsAndReplaceSolid() {
    dispatch(setKeepSolidDoors(false));
    applySelectedFrontToAllFramesThasHasDoors();
  }

  function setSolidDoorsAndReplaceGlass() {
    dispatch(setKeepGlassDoors(false));
    applySelectedFrontToAllFramesThasHasDoors();
  }

  function showKeepOpenShelvesSheet() {
    if (rootState?.changeFronts.keepOpenFrames === undefined) {
      dispatch(
        setQuestionSheetVisibleWithProps({
          modalVisible: true,
          title: 'storageone.keepOpenShelvesSheetTitle',
          preamble: 'storageone.keepOpenShelvesSheetBody',
          primaryButtonText: 'storageone.keepOpenShelvesSheetButtonPrimary',
          secondaryButtonText: 'storageone.keepOpenShelvesSheetButtonSecondary',
          onAccept: () => {
            reportDoorPromptReply({ userChoice: 'keep', promptType: 'open_shelves' });
            onAcceptKeepOpenShelvesSheet();
          },
          onDismiss: () => {
            reportDoorPromptReply({ userChoice: 'replace', promptType: 'open_shelves' });
            setDoorsOnOpenFrames();
          },
          imageRealtivePath: getRange() === 'besta' ? keepOpenFrames : keepSolidDoorsPlatsa
        } as QuestionSheetState)
      );
    } else if (rootState?.changeFronts.keepOpenFrames) {
      onAcceptKeepOpenShelvesSheet();
    } else {
      setDoorsOnOpenFrames();
    }
  }

  function showChangeSolidDoorsSheet() {
    if (rootState?.changeFronts.keepSolidDoors === undefined) {
      dispatch(
        setQuestionSheetVisibleWithProps({
          modalVisible: true,
          title: 'storageone.keepSolidDoorsSheetTitle',
          preamble: 'storageone.keepSolidDoorsSheetBody',
          primaryButtonText: 'storageone.keepSolidDoorsSheetButtonPrimary',
          secondaryButtonText: 'storageone.keepSolidDoorsSheetButtonSecondary',
          onAccept: () => {
            reportDoorPromptReply({ userChoice: 'keep', promptType: 'solid_doors' });
            setGlassDoorsAndKeepSolid();
          },
          onDismiss: () => {
            reportDoorPromptReply({ userChoice: 'replace', promptType: 'solid_doors' });
            setGlassDoorsAndReplaceSolid();
          },
          imageRealtivePath: keepSolidDoors
        } as QuestionSheetState)
      );
    } else if (rootState?.changeFronts.keepSolidDoors) {
      setGlassDoorsAndKeepSolid();
    } else {
      setGlassDoorsAndReplaceSolid();
    }
  }

  function showChangeGlassDoorsSheet() {
    if (rootState?.changeFronts.keepGlassDoors === undefined) {
      dispatch(
        setQuestionSheetVisibleWithProps({
          modalVisible: true,
          title: 'storageone.keepGlassDoorsSheetTitle',
          preamble: 'storageone.keepGlassDoorsSheetBody',
          primaryButtonText: 'storageone.keepGlassDoorsSheetButtonPrimary',
          secondaryButtonText: 'storageone.keepGlassDoorsSheetButtonSecondary',
          onAccept: () => {
            reportDoorPromptReply({ userChoice: 'keep', promptType: 'glass_doors' });
            setSolidDoorsAndKeepGlass();
          },
          onDismiss: () => {
            reportDoorPromptReply({ userChoice: 'replace', promptType: 'glass_doors' });
            setSolidDoorsAndReplaceGlass();
          },
          imageRealtivePath: keepGlassDoorsImage
        } as QuestionSheetState)
      );
    } else if (rootState?.changeFronts.keepGlassDoors) {
      setSolidDoorsAndKeepGlass();
    } else {
      setSolidDoorsAndReplaceGlass();
    }
  }

  function getAllOpenFrames(): Entity[] {
    const openFrames = frames
      .filter(
        frame =>
          !coreApi.entities.isOneOfProductTypes(frame, [
            ProductType.PLATSA_OPEN_FRAME_SHELVES,
            ProductType.PLATSA_OPEN_FRAME_HANGING,
            ProductType.PLATSA_OPEN_FRAME_SHOES,
            ProductType.SMASTAD_BENCH,
            ProductType.SMASTAD_PULL_OUT_FRAME_108_ASM,
            ProductType.EKET_FRAME,
            ProductType.EKET_FRAME_WITH_GLASS_DOOR_ASM
          ])
      )
      .reduce((agg, frame) => {
        const frameHasNoFronts = !frameHasFronts(frame);

        if (frameHasNoFronts) {
          agg.push(frame);
        }
        return agg;
      }, [] as Entity[]);

    return openFrames;
  }

  function getAllPartiallyOpenFrames(): Entity[] {
    const partiallyOpenFrames = frames
      .filter(
        frame =>
          !coreApi.entities.isOneOfProductTypes(frame, [
            ProductType.PLATSA_OPEN_FRAME_SHELVES,
            ProductType.PLATSA_OPEN_FRAME_HANGING,
            ProductType.PLATSA_OPEN_FRAME_SHOES,
            ProductType.SMASTAD_BENCH,
            ProductType.SMASTAD_PULL_OUT_FRAME_108_ASM,
            ProductType.SMASTAD_PULL_OUT_FRAME_196_ASM,
            ProductType.SMASTAD_DESK,
            ProductType.SMASTAD_CHANGING_TABLE,
            ProductType.EKET_FRAME,
            ProductType.EKET_FRAME_WITH_GLASS_DOOR_ASM
          ])
      )
      .reduce((agg, frame) => {
        const frameIsPartiallyOpen = getPartiallyOpenFrameSize(coreApi, frame).height > 0;

        if (frameIsPartiallyOpen) {
          agg.push(frame);
        }
        return agg;
      }, [] as Entity[]);
    return partiallyOpenFrames;
  }

  function getFrontTypesInScene(): FrontTypesInSceneType {
    const selectedDoorIsGlass = coreApi.products.isGlassDoor(selectedFrontId, products);

    return frontEntitesInScene.reduce(
      (agg, f) => {
        if (
          f.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.design.includes('glass')
        ) {
          agg.glass += 1;
        } else {
          const frontIsOnDrawer = f.hasComponent(ComponentIds.DRAWER_COMPONENT_ID);
          if (selectedDoorIsGlass && !frontIsOnDrawer) {
            agg.other += 1;
          } else if (!selectedDoorIsGlass) {
            agg.other += 1;
          }
        }

        return agg;
      },
      { glass: 0, other: 0 } as FrontTypesInSceneType
    );
  }

  function showKeepMirrorDoorsSheet() {
    if (rootState?.changeFronts.keepGlassDoors === undefined) {
      dispatch(
        setQuestionSheetVisibleWithProps({
          modalVisible: true,
          title: 'storageone.keepMirrorGlassDoorsSheetTitle',
          preamble: 'storageone.keepMirrorGlassDoorsSheetBody',
          primaryButtonText: 'storageone.keepMirrorGlassDoorsSheetPrimaryButton',
          secondaryButtonText: 'storageone.keepMirrorGlassDoorsSheetSecondaryButton',
          onAccept: setSolidDoorsAndKeepGlass,
          onDismiss: setSolidDoorsAndReplaceGlass,
          imageRealtivePath: keepMirrorDoorsImg
        } as QuestionSheetState)
      );
    } else if (rootState?.changeFronts.keepGlassDoors) {
      setSolidDoorsAndKeepGlass();
    } else {
      setSolidDoorsAndReplaceGlass();
    }
  }
};

export default changeFrontBehaviour;
